import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPix } from 'react-icons/fa6';
import { FiCreditCard, FiShield } from 'react-icons/fi';
import { MdCheck } from 'react-icons/md';
import { toast } from 'react-toastify';
import styles from './style.module.scss';
import { useCart } from '../../contexts/CartProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useSaldo } from '../../contexts/SaldoContext';
import { formatCurrency } from '../../hooks/useCheckoutTotals';
import CreditoPrePago from '../CreditoPrePago';
import CartaoCredito from '../CartaoCredito';
import Pix from '../Pix';
import logo from '../../assets/logo.png';

const PAYMENT_METHODS = {
  PRE_PAGO: 'prePago',
  CARTAO: 'cartao',
  PIX: 'pix',
};

const PAYMENT_COMPONENTS = {
  [PAYMENT_METHODS.PRE_PAGO]: CreditoPrePago,
  [PAYMENT_METHODS.CARTAO]: CartaoCredito,
  [PAYMENT_METHODS.PIX]: Pix,
};

const StepPagamento = ({ items, handleNextStep, showPrePago = true }) => {
  const { user } = useAuth();
  const { validateCoupon } = useCart();
  const { saldo } = useSaldo();

  // O contexto começa com saldo null e só resolve depois do GET /saldo.
  // Sem esse fallback a opção "Saldo" nasceria desabilitada em toda visita.
  const saldoAtual = saldo ?? user?.saldo ?? 0;

  const [couponCode, setCouponCode] = useState('');
  // Pix é o padrão: confirma em segundos e não exige digitar cartão.
  const [methodPayment, setMethodPayment] = useState(PAYMENT_METHODS.PIX);

  const titleRef = useRef(null);

  useEffect(() => {
    titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const paymentMethods = useMemo(
    () => [
      {
        id: PAYMENT_METHODS.PIX,
        title: 'Pix',
        subtitle: 'Aprovação na hora',
        badge: 'Mais rápido',
        icon: <FaPix />,
        disabled: false,
        show: true,
      },
      {
        id: PAYMENT_METHODS.CARTAO,
        title: 'Cartão',
        subtitle: 'Parcele sua compra',
        icon: <FiCreditCard />,
        disabled: false,
        show: true,
      },
      {
        id: PAYMENT_METHODS.PRE_PAGO,
        title: 'Saldo',
        subtitle: user ? formatCurrency(saldoAtual) : 'Indisponível',
        icon: <img src={logo} alt='' className={styles.logoIcon} />,
        disabled: !user || saldoAtual <= 0,
        show: showPrePago,
      },
    ],
    [saldoAtual, showPrePago, user]
  );

  const visibleMethods = useMemo(() => paymentMethods.filter((m) => m.show), [paymentMethods]);

  // Se o método atual sumiu ou ficou indisponível, cai no primeiro habilitado.
  useEffect(() => {
    const atual = visibleMethods.find((m) => m.id === methodPayment);
    if (atual && !atual.disabled) return;

    const habilitado = visibleMethods.find((m) => !m.disabled) || visibleMethods[0];
    if (habilitado) setMethodPayment(habilitado.id);
  }, [methodPayment, visibleMethods]);

  const validarCupon = async (isPix = false) => {
    const normalizado = couponCode.trim().toLowerCase();

    if (normalizado === 'pix10' && !isPix) {
      toast.warn('Cupom não aplicável a esta modalidade de pagamento', {
        position: 'top-right',
        autoClose: 5000,
        theme: 'light',
      });
      return false;
    }

    return validateCoupon(couponCode);
  };

  const StepComponent = PAYMENT_COMPONENTS[methodPayment] || Pix;

  return (
    <div className={styles.container}>
      <header className={styles.header} ref={titleRef}>
        <h1 className={styles.title}>Como você quer pagar?</h1>
        <span className={styles.trust}>
          <FiShield /> Compra protegida
        </span>
      </header>

      {/* Seletor compacto: antes eram três cartões de 2rem de padding que empurravam
          o pagamento para baixo da dobra. */}
      <div className={styles.methods} role='tablist'>
        {visibleMethods.map((method) => {
          const ativo = methodPayment === method.id;

          return (
            <button
              key={method.id}
              type='button'
              role='tab'
              aria-selected={ativo}
              disabled={method.disabled}
              onClick={() => setMethodPayment(method.id)}
              className={`${styles.method} ${ativo ? styles.selected : ''} ${method.disabled ? styles.disabled : ''}`}
            >
              {method.badge && !method.disabled && <span className={styles.badge}>{method.badge}</span>}

              <span className={styles.methodIcon}>{method.icon}</span>

              <span className={styles.methodText}>
                <strong>{method.title}</strong>
                <small>{method.subtitle}</small>
              </span>

              {ativo && (
                <span className={styles.check}>
                  <MdCheck />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className={styles.paymentContent}>
        <StepComponent
          key={methodPayment}
          validarCupon={validarCupon}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          items={items}
          handleNextStep={handleNextStep}
        />
      </div>
    </div>
  );
};

export default StepPagamento;
